// Meetings on a Calendar board (Calendar plan, step 5): the link that joins an event's video call, and which service
// it is. Google's own meeting details come first (conferenceData's video entry, hangoutLink), then a known meeting link
// in the event's place or notes, so a Zoom or Teams invite pasted into an event still gets Join. Only an https link to
// the service's own host, on a meeting's own path, is taken: a Join button never opens anything else.

export type JoinKind = "meet" | "zoom" | "teams" | "webex";

export interface Join {
  url: string;
  kind: JoinKind;
}

export const JOIN_NAMES: Record<JoinKind, string> = { meet: "Google Meet", zoom: "Zoom", teams: "Teams", webex: "Webex" };

const HOSTS: [JoinKind, RegExp][] = [
  ["meet", /^meet\.google\.com$/],
  ["zoom", /^(?:[a-z0-9-]+\.)?(?:zoom\.us|zoom\.com|zoomgov\.com)$/],
  ["teams", /^teams\.(?:microsoft|live)\.com$/],
  ["webex", /^(?:[a-z0-9-]+\.)?webex\.com$/],
];

// a meeting's own address on each, never the service's other pages
const PATHS: Record<JoinKind, RegExp> = {
  meet: /^\/(?:[a-z]{3}-[a-z]{4}-[a-z]{3}(?:$|[/?#])|lookup\/[\w-]+)/i,
  zoom: /^\/(?:j|my|w|s|wc)\/[\w.-]+/i,
  teams: /^\/(?:l\/meetup-join\/|meet\/)/i,
  webex: /^\/(?:meet\/|join\/|[\w-]+\/j\.php|wbxmjs\/joinservice\/)/i,
};

/** A meeting link and its service, or null: https, on the service's own host, a meeting's own path. */
export function joinOf(raw: string): Join | null {
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return null;
  }
  if (u.protocol !== "https:" || u.username || u.password || u.port) return null;
  const host = u.hostname.toLowerCase();
  for (const [kind, re] of HOSTS) if (re.test(host) && PATHS[kind].test(u.pathname)) return { url: u.toString().slice(0, 1000), kind };
  return null;
}

/** The first meeting link in some words (an event's place or notes), with or without its https. */
export function joinIn(text: string | undefined | null): Join | null {
  if (!text) return null;
  for (const m of text.matchAll(/(?:https:\/\/)?(?:[a-z0-9-]+\.)*(?:meet\.google\.com|zoom\.us|zoom\.com|zoomgov\.com|teams\.microsoft\.com|teams\.live\.com|webex\.com)\/[^\s<>()"'\]]+/gi)) {
    const link = m[0].replace(/[.,;:!?]+$/, "");
    const j = joinOf(/^https:\/\//i.test(link) ? link : `https://${link}`);
    if (j) return j;
  }
  return null;
}

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj => (v && typeof v === "object" && !Array.isArray(v) ? (v as Obj) : {});

/** An event's meeting link from Google: its meeting details' video entry first, then hangoutLink, then its place and notes. */
export function joinFromGoogle(e: Obj, location?: string, notes?: string): Join | null {
  const points = obj(e.conferenceData).entryPoints;
  for (const p of Array.isArray(points) ? points.map(obj) : []) {
    if (p.entryPointType !== "video" || typeof p.uri !== "string") continue;
    const j = joinOf(p.uri);
    if (j) return j;
  }
  if (typeof e.hangoutLink === "string") {
    const j = joinOf(e.hangoutLink);
    if (j) return j;
  }
  return joinIn(location) ?? joinIn(notes);
}

/** Whether Join shows on an event's block: from ten minutes before it starts until it ends. */
export const joinShowsAt = (t0: number, t1: number, now: number) => now >= t0 - 10 * 60_000 && now < t1;

/** A guest's answer, as Google keeps it. */
export type Reply = "needsAction" | "accepted" | "tentative" | "declined";
export const REPLIES: Reply[] = ["needsAction", "accepted", "tentative", "declined"];
export const REPLY_WORDS: Record<Reply, string> = { needsAction: "Not answered", accepted: "Going", tentative: "Maybe", declined: "Not going" };

/** An email address as a guest's: trimmed, lower case, and only one that looks like an address. */
export function guestEmail(raw: unknown): string | null {
  const s = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  return s.length <= 254 && /^[^\s@<>()",;:]+@[^\s@<>()",;:]+\.[a-z]{2,}$/i.test(s) ? s : null;
}
